<script setup lang="ts">
import { computed } from "vue";
import type { ILabelProps } from "./VLabel.types";
import { responsiveClass } from "../../utilities/helpers";
import IconArrowRightCorner16 from "@magnit/icons/src/assets/icons/arrow-right-corner-16.svg";
import IconArrowRightCornerSmall24 from "@magnit/icons/src/assets/icons/arrow-right-corner-small-24.svg";

const props = withDefaults(defineProps<ILabelProps>(), {
  size: "s",
  theme: "discount",
  arrow: false,
});

const valueClass = computed(() => [
  {
    [`pl-label_${props.theme}`]: props.theme,
    "pl-label_clickable": props.arrow,
  },
  ...responsiveClass("pl-label_size", props.size),
]);
</script>

<template>
  <div
    class="pl-label"
    :class="valueClass"
    :style="{ background: bgColor, color: textColor }"
  >
    <div class="pl-label__content">
      <slot name="iconLeft" />
      <slot class="pl-label__text" />
      <slot name="iconRight" />
    </div>
    <template v-if="arrow">
      <IconArrowRightCorner16 class="pl-label__arrow pl-label__arrow_thin" />
      <IconArrowRightCornerSmall24
        class="pl-label__arrow pl-label__arrow_bold"
      />
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.pl-label {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  text-align: center;
  white-space: nowrap;
  gap: var(--pl-unit-x1);
  font: var(--_label-inner-font);
  padding: var(--_label-inner-padding);
  border-radius: var(--_label-inner-radius);

  --_label-inner-xs-font: var(--pl-font-caption-small-accent-low);
  --_label-inner-xs-padding: 1px 6px;
  --_label-inner-xs-radius: var(--pl-label-radius-s);
  --_label-inner-s-font: var(--pl-font-caption-large-accent);
  --_label-inner-s-padding: 1px var(--pl-unit-x2);
  --_label-inner-s-radius: var(--pl-label-radius-m);
  --_label-inner-m-font: var(--pl-font-title-medium);
  --_label-inner-m-padding: 3px var(--pl-unit-x3);
  --_label-inner-m-radius: var(--pl-label-radius-l);
  --_label-content-xs-icon-size: var(--pl-unit-x3);
  --_label-content-s-icon-size: var(--pl-unit-x4);
  --_label-content-m-icon-size: var(--pl-unit-x6);
  --_arrow-thin-xs-display: none;
  --_arrow-thin-s-display: block;
  --_arrow-thin-m-display: none;
  --_arrow-bold-xs-display: none;
  --_arrow-bold-s-display: none;
  --_arrow-bold-m-display: block;

  @each $size in xs, s, m {
    &_size-$(size) {
      --_label-inner-font: var(--_label-inner-$(size)-font);
      --_label-inner-padding: var(--_label-inner-$(size)-padding);
      --_label-inner-radius: var(--_label-inner-$(size)-radius);
      --_label-content-icon-size: var(--_label-inner-$(size)-icon-size);
      --_arrow-thin-display: var(--_arrow-thin-$(size)-display);
      --_arrow-bold-display: var(--_arrow-bold-$(size)-display);
    }
  }

  @each $breakpoint in s, m, ml, l, xl {
    @media (--pl-viewport-from-$(breakpoint)) {
      @each $size in xs, s, m {
        &_size-$(size)-$(breakpoint) {
          --_label-inner-font: var(--_label-inner-$(size)-font);
          --_label-inner-padding: var(--_label-inner-$(size)-padding);
          --_label-inner-radius: var(--_label-inner-$(size)-radius);
          --_arrow-thin-display: var(--_arrow-thin-$(size)-display);
          --_arrow-bold-display: var(--_arrow-bold-$(size)-display);
        }
      }
    }
  }

  &_clickable {
    cursor: pointer;
  }

  /* themes */
  &_apteka {
    color: var(--pl-label-text-apteka);
    background: var(--pl-label-background-apteka);
  }

  &_article {
    color: var(--pl-text-invert-primary);
    background: var(--pl-label-background-article);
  }

  &_banner {
    color: var(--pl-text-invert-primary);
    background: var(--pl-label-background-banner);
  }

  &_benefit {
    color: var(--pl-label-text-benefit);
    background: var(--pl-label-background-benefit);
  }

  &_bonus {
    color: var(--pl-text-invert-primary);
    background: var(--pl-gradient-loyalty-bonus);
  }

  &_card-plus {
    color: var(--pl-text-primary);
    background: var(--pl-label-background-promotion);
  }

  &_discount {
    color: var(--pl-text-invert-primary);
    background: var(--pl-label-background-discount);
  }

  &_discount-weak {
    color: var(--pl-label-text-discount);
    background: var(--pl-label-background-discount-weak);
  }

  &_health {
    color: var(--pl-label-text-club-health);
    background: var(--pl-label-background-club-health);
  }

  &_magnet {
    color: var(--pl-text-invert-primary);
    background: var(--pl-gradient-loyalty-magnet);
  }

  &_mother {
    color: var(--pl-label-text-club-mother);
    background: var(--pl-label-background-club-mother);
  }

  &_new {
    color: var(--pl-label-text-new-product);
    background: var(--pl-label-background-new-product);
  }

  &_other {
    color: var(--pl-label-text-other);
    background: var(--pl-label-background-other);
  }

  &_pets {
    color: var(--pl-label-text-club-pets);
    background: var(--pl-label-background-club-pets);
  }

  &_plus {
    color: var(--pl-text-invert-primary);
    background: var(--pl-gradient-loyalty-plus);
  }

  &_premium {
    background: var(--pl-gradient-premium-background);

    & .pl-label__content {
      color: transparent;
      background: var(--pl-gradient-premium-foreground);
      background-clip: text;
      text-transform: uppercase;
      font-weight: 600;
    }
  }

  &_promo {
    color: var(--pl-text-invert-primary);
    background: var(--pl-label-background-promo-mechanics);
  }

  &_promotion {
    color: var(--pl-text-primary);
    background: var(--pl-label-background-promotion);
  }

  &_recipe {
    color: var(--pl-text-primary);
    background: var(--pl-label-background-recipe);
  }

  &_selection {
    color: var(--pl-text-primary);
    background: var(--pl-label-background-selection);
  }

  &_text-info {
    color: var(--pl-text-primary);
    background: var(--pl-surface-muted-default);
  }

  &__content {
    display: flex;
    gap: var(--pl-unit-x1);
    align-items: center;

    :deep(svg) {
      width: var(--_label-content-icon-size);
      height: var(--_label-content-icon-size);
    }
  }

  &__arrow {
    &_thin {
      display: var(--_arrow-thin-display);
    }

    &_bold {
      display: var(--_arrow-bold-display);
    }
  }
}
</style>
